<script lang="ts">
	// Svelte
	import { createEventDispatcher } from 'svelte';
	// Services
	import { format } from 'svelte-i18n';
	// Design System
	import { Text, Token } from '@Shelly/shelly-design-system';
	// Helpers
	import { capFirstChar } from 'src/helpers/utilities';
	import { getDevicePropTranslation } from '../helpers/translation-helpers';
	import {
		CAMERA_MODE_MOTION_ZONES,
		type CameraMotionZone,
		buildCameraCondition,
		getEffectiveCameraDeviceProps,
		initializeCameraDeviceFromCondition,
		validateCameraCondition,
	} from 'src/components/Scenes/helpers/device_conditions/custom_cases/camera/camera-device-helpers';

	export let sceneData: any = {};
	export let motionZones: CameraMotionZone[] = [];
	export let disabled = false;

	const dispatch = createEventDispatcher<{
		conditionChange: { condition: Record<string, unknown> };
		validationChange: { isValid: boolean };
		devicePropChange: { deviceProp: { title: string; value: string } };
	}>();

	const BOOLEAN_VALUE_OPTIONS = ['true', 'false'] as const;

	let selectedCameraMode = '';
	let selectedZone = '';
	let selectedValue = '';
	let isUserInteracting = false;
	let hasInitialized = false;

	$: effectiveDevicePropsOptions = getEffectiveCameraDeviceProps($format, motionZones);

	$: currentZoneName = (() => {
		if (!selectedZone || !motionZones.length) {
			return '';
		}
		const zone = motionZones.find((z) => z.value === selectedZone);
		return zone ? zone.title : selectedZone.replace('zone_', 'Zone ');
	})();

	$: valueI18tr = (() => {
		if (selectedCameraMode === CAMERA_MODE_MOTION_ZONES) {
			return 'camera_zone_motion';
		}
		return selectedCameraMode;
	})();

	$: showValueSelector =
		Boolean(selectedCameraMode) &&
		(selectedCameraMode !== CAMERA_MODE_MOTION_ZONES || Boolean(selectedZone));

	$: {
		if (
			sceneData.when_info?.mode === 'edit' &&
			!hasInitialized &&
			!isUserInteracting &&
			(!selectedCameraMode || !selectedValue)
		) {
			initializeFromCondition();
		}
	}

	$: {
		if (selectedCameraMode && selectedValue) {
			handleCameraCondition();
		}
	}

	$: {
		const isValid = validateCameraCondition(selectedCameraMode, selectedZone, selectedValue);
		dispatch('validationChange', { isValid });
	}

	const initializeFromCondition = () => {
		const initialization = initializeCameraDeviceFromCondition(sceneData);
		if (!initialization) {
			return;
		}

		selectedCameraMode = initialization.selectedCameraMode;
		selectedZone = initialization.selectedZone;
		selectedValue = initialization.selectedValue;
		hasInitialized = true;
	};

	const handleCameraCondition = () => {
		if (!selectedCameraMode || !selectedValue) {
			return;
		}

		const condition = buildCameraCondition(selectedCameraMode, selectedZone, selectedValue);
		if (!condition) {
			return;
		}

		dispatch('conditionChange', {
			condition: {
				id: condition.conditionName,
				operator: condition.conditionOperator,
				value: condition.conditionValue,
				sensor_name: undefined,
				add_info_ind: condition.zoneAddInfoInd,
				input_index: undefined,
			},
		});
	};

	const onSelectDeviceProp = (deviceProp: { title: string; value: string }) => {
		isUserInteracting = true;
		selectedCameraMode = deviceProp.value;
		selectedZone = '';
		selectedValue = '';

		dispatch('devicePropChange', { deviceProp });

		if (sceneData._device_condition) {
			sceneData._device_condition.operator = '';
			sceneData._device_condition.value = '0';
			sceneData._device_condition.cond_name = '';
			sceneData._device_condition.add_info_ind = undefined;
			sceneData._device_condition.input_index = undefined;
		}

		sceneData = sceneData;
	};

	const onSelectZone = (zoneValue: string) => {
		isUserInteracting = true;
		selectedZone = zoneValue;
		selectedValue = '';

		if (sceneData._device_condition) {
			sceneData._device_condition.operator = '';
			sceneData._device_condition.value = '0';
			sceneData._device_condition.cond_name = '';
			sceneData._device_condition.add_info_ind = undefined;
		}

		sceneData = sceneData;
	};

	const onSelectValue = (value: string) => {
		isUserInteracting = true;
		selectedValue = value;

		if (sceneData._device_condition) {
			sceneData._device_condition.value = value;
		}

		handleCameraCondition();
		sceneData = sceneData;
	};
</script>

<div class="d-flex flex-wrap gap-s">
	{#each effectiveDevicePropsOptions as deviceProp}
		<Token
			id="token-camera-prop-{deviceProp.value}"
			title={capFirstChar(deviceProp.title)}
			class="text-capitalize"
			on:click={() => onSelectDeviceProp(deviceProp)}
			selected={selectedCameraMode === deviceProp.value}
			disabled={deviceProp.disabled || disabled}
		/>
	{/each}
</div>

{#if selectedCameraMode}
	<div class="mt-m">
		{#if selectedCameraMode === CAMERA_MODE_MOTION_ZONES && motionZones.length > 0}
			<Text class="mb-s" styleType="h2">
				{$format('select_zone', { default: 'Select Zone' })}
			</Text>
			<div class="d-flex flex-wrap mb-l gap-s">
				{#each motionZones as zone}
					<Token
						id="token-camera-zone-{zone.id}"
						title={capFirstChar(zone.title)}
						size="m"
						selected={selectedZone === zone.value}
						on:click={() => onSelectZone(zone.value)}
						{disabled}
					/>
				{/each}
			</div>
		{/if}

		{#if showValueSelector}
			<Text class="mb-s" styleType="h2">
				{#if selectedCameraMode === CAMERA_MODE_MOTION_ZONES}
					{$format('select_motion_state_for_zone', {
						values: { zoneName: currentZoneName },
						default: `Motion state for ${currentZoneName}`,
					})}
				{:else}
					{getDevicePropTranslation(valueI18tr, '', undefined, '')?.title}
				{/if}
			</Text>
			<div class="d-flex flex-wrap mb-l gap-s">
				{#each BOOLEAN_VALUE_OPTIONS as boolValue}
					{@const currentTranslation = getDevicePropTranslation(
						valueI18tr,
						'',
						undefined,
						boolValue,
					)}
					<Token
						id="token-camera-value-{boolValue}"
						title={currentTranslation?.condition}
						size="m"
						icon={currentTranslation?.icons[boolValue]}
						selected={selectedValue === boolValue}
						on:click={() => onSelectValue(boolValue)}
						{disabled}
					/>
				{/each}
			</div>
		{/if}
	</div>
{/if}
